import * as React from "react";
import { Breadcrumb, Button, ButtonGroup, DropdownMenu, Separator } from "../ui";
import { useHistory } from "react-router-dom";
import { MoreHorizontalIcon } from "lucide-react";

export interface PageActionProps {
  text: string;
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: (event: any, data: any) => void;
}

export interface PageTitleProps {
  title: string;
  primaryAction?: PageActionProps;
  secondaryAction?: PageActionProps;
  actions?: PageActionProps[];
  hideSeparator?: boolean;
  breadcrumbs?: { name: string; link: string | (() => void) }[];
}

const PageTitleFC: React.FC<PageTitleProps> = ({
  title,
  primaryAction,
  secondaryAction,
  actions,
  hideSeparator = false,
  breadcrumbs = [],
}) => {
  const history = useHistory();

  const getActionButtons = (): JSX.Element | null => {
    let finalPrimary = primaryAction;
    let finalSecondary = secondaryAction;
    let dropdownItems = actions ? [...actions] : [];

    // Fallback: If no explicit primary action, use the first action in the array
    if (!finalPrimary && dropdownItems.length > 0) {
      finalPrimary = dropdownItems.shift();
    }

    // Fallback: If no explicit secondary action and exactly 1 action remains, use it as secondary
    if (!finalSecondary && dropdownItems.length === 1) {
      finalSecondary = dropdownItems.shift();
    }

    if (!finalPrimary) return null;

    // Build the primary button
    const primaryButton = (
      <Button
        disabled={finalPrimary.disabled}
        onClick={() => finalPrimary.onClick && finalPrimary.onClick(null, null)}
        loading={finalPrimary.loading}
      >
        {finalPrimary.text}
      </Button>
    );

    // Build the dropdown for any remaining items
    const dropdownOptions = dropdownItems.map((item, index) => ({
      key: index,
      selected: false,
      active: false,
      ...item,
    }));

    // If there are dropdown items, wrap the primary button in a ButtonGroup with the Dropdown
    const primaryNode =
      dropdownOptions.length > 0 ? (
        <div className="flex items-center gap-2">
          {primaryButton}
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <Button variant="outline" size="icon">
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {dropdownOptions.map((option, index) => (
                <DropdownMenu.Item key={index} onClick={() => option.onClick && option.onClick(null, null)}>
                  {option.text}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      ) : (
        primaryButton
      );

    // If we have a secondary action, render it alongside the primary node
    if (finalSecondary) {
      const secondaryButton = (
        <Button
          variant="outline"
          disabled={finalSecondary.disabled}
          onClick={() => {
            finalSecondary.onClick && finalSecondary.onClick(null, null);
          }}
          loading={finalSecondary.loading}
        >
          {finalSecondary.text}
        </Button>
      );

      return (
        <div className="flex items-center gap-2">
          {secondaryButton}
          {primaryNode}
        </div>
      );
    }

    return primaryNode;
  };

  return (
    <div className="max-w-[1300px] pb-4 flex flex-col gap-7">
      <div className="flex items-center w-full gap-4 h-8 justify-between">
        <Breadcrumb>
          <Breadcrumb.List>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={`breadcrumb-fragment-${index}`}>
                <Breadcrumb.Item>
                  <Breadcrumb.Link
                    className="text-2xl font-bold grow min-w-fit whitespace-nowrap"
                    onClick={() => {
                      if (typeof breadcrumb.link === "function") {
                        breadcrumb.link();
                      } else {
                        history.push(breadcrumb.link);
                      }
                    }}
                  >
                    {breadcrumb.name}
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator className="[&>svg]:size-5" />
              </React.Fragment>
            ))}
            <Breadcrumb.Item>
              <Breadcrumb.Page className="text-2xl font-bold grow min-w-fit whitespace-nowrap">{title}</Breadcrumb.Page>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb>

        {getActionButtons()}
      </div>

      {hideSeparator || <Separator className="w-full h-px" />}
    </div>
  );
};

export const PageTitle = PageTitleFC;
